import { type RecommendationRequestStatus } from '#app/__generated__/api.js';
import { type RequestedRecommendations_userProfile$key } from '#app/__generated__/relay/RequestedRecommendations_userProfile.graphql.js';
import { Avatar } from '#app/components/Avatars/Avatar/Avatar.js';
import { Text } from '#app/components/Primitives/Text.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { formatName } from '#app/utilities/formatName.js';
import { dayjs } from '@contra/date';
import { ClockIcon, EyeOffIcon, SparklesIcon } from '@contra/icons/icons';
import { graphql, useFragment } from '@contra/relay';
import { isTruthy } from '@contra/utilities/isTruthy';
import { css, styled } from 'styled-components';

const RequestedRecommendationsContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 24px;
    border-radius: 20px;

    ${theme.mediaQueries.sm} {
      gap: 20px;
    }
  `}
`;

const RequestedRecommendation = styled.div`
  ${({ theme }) => css`
    border-radius: 20px;
    background-color: ${theme.colorsV2.white};
    border: 1px solid ${theme.colorsV2.gray30};
    padding: 16px 16px 24px;

    ${theme.mediaQueries.sm} {
      padding: 24px;
    }
  `}
`;

const RequesterNotificationRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    padding-bottom: 16px;
    align-items: center;

    ${theme.mediaQueries.sm} {
      padding-bottom: 8px;
    }
  `}
`;

const RequesterNotificationRowLeft = styled(Text).attrs({ as: 'div' })`
  display: flex;
  flex-direction: row;
  gap: 8px;
`;

const RequesterNotificationRowRight = styled(Text).attrs({ as: 'div' })`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 4px;
    padding: 8px 10px;
    border-radius: 20px;
    background-color: ${theme.colorsV2.gray05};
    color: ${theme.colorsV2.gray70};
    font-weight: 600;
  `}
`;

const RecommenderDetails = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    gap: 8px;
    align-items: center;

    ${theme.mediaQueries.sm} {
      gap: 12px;
      margin-top: 8px;
    }
  `}
`;

const AvatarContainer = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    & > * {
      border: 2px solid ${theme.colorsV2.white};
      border-radius: 50%;
    }

    & > *:not(:first-child) {
      margin-left: -16px;
    }
  `}
`;

const statusText: Record<RecommendationRequestStatus, string> = {
  '%future added value': 'Pending',
  APPROVED: 'Approved',
  DECLINED: 'Declined',
  EXPIRED: 'Expired',
  PENDING: 'Pending',
  SUBMITTED: 'Pending review',
} as const;

const RecommenderNameAndDate = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
`;

const RecommenderName = styled(Text).attrs({ as: 'div' })``;

const RecommenderDate = styled(Text).attrs({ as: 'div' })``;

const RequestMessage = styled(Text).attrs({ as: 'div' })`
  ${({ theme }) => css`
    margin-top: 16px;

    ${theme.mediaQueries.sm} {
      margin-top: 24px;
    }
  `}
`;

const RecommendationMessage = styled(Text).attrs({ as: 'div' })`
  margin-top: 8px;
  overflow-wrap: break-word;
`;

const RecommendationSubtext = styled(Text).attrs({ as: 'div' })`
  display: flex;
  align-items: center;
  gap: 8px;

  ${({ theme }) => css`
    margin-top: 16px;

    ${theme.mediaQueries.sm} {
      margin-top: 24px;
    }
  `}
`;

type RecommendationRequestProps = Readonly<{
  createdAt: string;
  recommendation: null | string;
  recommender: null | {
    avatarImage: null | {
      uid: string;
    };
    firstName: null | string;
    lastName: null | string;
    organization: null | {
      logoImage: null | {
        uid: string;
      };
      name: string;
    };
  };
  requesterFirstName: string;
  status: RecommendationRequestStatus;
}>;

const RecommendationRequest = ({
  createdAt,
  recommendation,
  recommender,
  requesterFirstName,
  status,
}: RecommendationRequestProps) => {
  const isMobile = useIsMobile();
  const showNotificationRow = status !== 'APPROVED';
  const recommenderFullName = formatName(
    { firstName: recommender?.firstName, lastName: recommender?.lastName },
    'fullName',
  );
  const name = [recommenderFullName, recommender?.organization?.name]
    .filter(isTruthy)
    .join(' • ');

  const dateValue = dayjs(createdAt).format('MMM D, YYYY');
  const dateText =
    status === 'APPROVED'
      ? `Client • ${dateValue}`
      : `Request sent: ${dateValue}`;

  const statusLabel = statusText[status];

  return (
    <RequestedRecommendation>
      {showNotificationRow ? (
        <RequesterNotificationRow>
          <RequesterNotificationRowLeft
            color="uiBlackMediumEmphasis"
            textStyle="captionRegular"
          >
            <EyeOffIcon size={16} />
            Only you can see this
          </RequesterNotificationRowLeft>

          <RequesterNotificationRowRight textStyle="captionBold">
            {status === 'PENDING' ? <ClockIcon size={16} /> : null}
            {statusLabel}
          </RequesterNotificationRowRight>
        </RequesterNotificationRow>
      ) : null}

      {recommender ? (
        <RecommenderDetails>
          <AvatarContainer>
            <Avatar
              avatarImage={
                recommender.avatarImage?.uid
                  ? { uid: recommender.avatarImage.uid }
                  : null
              }
              size="sm"
            />
            {recommender.organization ? (
              <Avatar
                avatarImage={
                  recommender.organization.logoImage?.uid
                    ? { uid: recommender.organization.logoImage.uid }
                    : null
                }
                size="sm"
              />
            ) : null}
          </AvatarContainer>
          <RecommenderNameAndDate>
            <RecommenderName
              color="brandBlack"
              textStyle={isMobile ? 'subtitleRegular' : 'sixthLevelHeadline'}
            >
              {name}
            </RecommenderName>

            <RecommenderDate
              color="uiBlackMediumEmphasis"
              textStyle={isMobile ? 'captionRegular' : 'bodySmall'}
            >
              {dateText}
            </RecommenderDate>
          </RecommenderNameAndDate>
        </RecommenderDetails>
      ) : null}
      {recommendation ? (
        <>
          <RecommendationSubtext
            color="uiBlackMediumEmphasis"
            textStyle="bodySmall"
          >
            <SparklesIcon />
            {`${recommender?.firstName} recommends working with ${requesterFirstName}`}
          </RecommendationSubtext>
          <RecommendationMessage textStyle="bodyRegular">
            {recommendation}
          </RecommendationMessage>
        </>
      ) : (
        <RequestMessage
          color="uiBlackMediumEmphasis"
          textStyle="bodySmall"
        >
          {`Your recommendation request is awaiting ${recommender?.firstName}'s response.`}
        </RequestMessage>
      )}
    </RequestedRecommendation>
  );
};

type Props = {
  readonly profileRef: RequestedRecommendations_userProfile$key;
};

/**
 * @package
 */
export const RequestedRecommendations = ({ profileRef }: Props) => {
  const profile = useFragment<RequestedRecommendations_userProfile$key>(
    graphql`
      fragment RequestedRecommendations_userProfile on UserProfile {
        firstName
        recommendationRequests {
          edges {
            node {
              createdAt
              id
              recommendation
              recommender {
                firstName
                lastName
                avatarImage {
                  uid
                }
                organization {
                  name
                  logoImage {
                    uid
                  }
                }
              }
              status
            }
          }
        }
      }
    `,
    profileRef,
  );

  if (profile.recommendationRequests.edges.length === 0) {
    return null;
  }

  const filteredRequests = profile.recommendationRequests.edges.filter(
    ({ node: { status } }) => {
      return status !== 'APPROVED';
    },
  );

  return (
    <RequestedRecommendationsContainer>
      {filteredRequests.map(
        ({ node: { createdAt, id, recommendation, recommender, status } }) => (
          <RecommendationRequest
            createdAt={createdAt}
            key={id}
            recommendation={recommendation}
            recommender={recommender}
            requesterFirstName={profile.firstName ?? ''}
            status={status}
          />
        ),
      )}
    </RequestedRecommendationsContainer>
  );
};
